import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import CategoryQuickLinks from "./components/CategoryQuickLinks";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import { collections } from "@/mocks/collections";
import { categoryMeta, getVideoById, videos, type VideoCategory } from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const heroSlides = ["m01", "t05", "a03", "m03", "t01"]
  .map((id) => getVideoById(id))
  .filter((v): v is NonNullable<typeof v> => Boolean(v));

const accentText: Record<string, string> = {
  primary: "text-primary-600 bg-primary-100",
  accent: "text-accent-700 bg-accent-100",
  secondary: "text-secondary-700 bg-secondary-100",
};

export default function Home() {
  usePageMeta({
    title: "高清视频免费观看 - 海量高清视频在线播放平台",
    description:
      "高清视频免费观看提供丰富的高清视频资源，涵盖电影、电视剧和综艺节目，满足用户多样化的在线看视频需求。",
    keywords: "高清视频免费观看,高清视频在线观看,在线视频,高清视频播放,在线电影,电视剧在线观看,高清视频资源",
  });

  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  const latest = useMemo(
    () => [...videos].sort((a, b) => b.publishDate.localeCompare(a.publishDate)).slice(0, 8),
    []
  );

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div className="bg-background-100">
      <HeroCarousel slides={heroSlides} />

      <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Search entry */}
        <div className="-mt-8 md:-mt-10 relative z-20">
          <form
            onSubmit={submitSearch}
            className="bg-background-50 border border-background-200 rounded-lg p-3 md:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索你想看的电影、电视剧或综艺..."
                className="w-full pl-10 pr-3 py-3 rounded-md bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-primary-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-7 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-search-line"></i>
              立即搜索
            </button>
          </form>
        </div>

        {/* Category quick links */}
        <section className="pt-12 md:pt-16">
          <SectionHeading
            icon="ri-grid-line"
            title="热门分类快捷入口"
            subtitle="三大主分类，一键直达你喜欢的内容"
          />
          <CategoryQuickLinks />
        </section>

        {/* Latest */}
        <section className="pt-12 md:pt-16">
          <SectionHeading
            icon="ri-time-line"
            title="最新上线视频"
            subtitle="刚刚上架的高清资源，抢先看个够"
            linkTo="/category/movie"
            linkLabel="浏览全部"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {latest.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Category strips */}
        <section className="pt-12 md:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
            {(Object.keys(categoryMeta) as VideoCategory[]).map((type) => {
              const meta = categoryMeta[type];
              const list = videos.filter((v) => v.category === type).slice(0, 3);
              return (
                <div key={type}>
                  <SectionHeading
                    icon={type === "movie" ? "ri-film-line" : type === "tv" ? "ri-tv-2-line" : "ri-mic-line"}
                    title={meta.label}
                    linkTo={`/category/${meta.slug}`}
                    linkLabel="更多"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    {list.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Collections */}
        {collections.map((collection, cIdx) => {
          const items = collection.videoIds
            .map((id) => getVideoById(id))
            .filter((v): v is NonNullable<typeof v> => Boolean(v));
          return (
            <section key={collection.id} className="pt-12 md:pt-16">
              <div className="flex items-center justify-between gap-4 mb-5 md:mb-7">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 rounded-md text-sm font-semibold ${accentText[collection.accent]}`}>
                    {String(cIdx + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">{collection.title}</h2>
                    <p className="text-xs md:text-sm text-foreground-500 mt-0.5">{collection.subtitle}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {items.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </section>
          );
        })}

        {/* Brand intro */}
        <section className="pt-12 md:pt-16 pb-16 md:pb-20">
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                <i className="ri-award-line"></i>
                关于高清视频免费观看
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-snug">
                一个专为高清观影打造的一站式视频平台
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们聚合电影、电视剧、综艺等丰富类别的高清视频资源，提供清晰的分类浏览、精准的关键词搜索与贴心
                的相关推荐，让你在海量内容中快速找到想看的那一部，随时随地享受流畅舒适的观影体验。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category/movie"
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  开始探索内容
                </Link>
                <Link
                  to="/about"
                  className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  了解平台
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:col-span-1">
              {[
                { num: "5000+", label: "高清视频资源", icon: "ri-video-line" },
                { num: "3", label: "主分类频道", icon: "ri-apps-2-line" },
                { num: "1080P", label: "高清画质", icon: "ri-hd-line" },
                { num: "免费", label: "在线观看", icon: "ri-gift-line" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-primary-50 border border-primary-100 p-4 text-center">
                  <span className="w-10 h-10 mx-auto rounded-lg bg-primary-500 text-white flex items-center justify-center mb-2">
                    <i className={`${stat.icon} text-lg`}></i>
                  </span>
                  <p className="font-heading font-bold text-lg text-foreground-950">{stat.num}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}